import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { generateSlug } from "@/utils/slugUtils";
import { PoliticaContent, PoliticaContentType } from "@/hooks/usePoliticasContent";

/**
 * Hook simplificado para buscar FAQ pública por slug
 * Usa useState/useEffect para garantir carregamento confiável na primeira visita
 */
export const usePublicFAQ = (slug: string | undefined) => {
  const [data, setData] = useState<PoliticaContent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setData(null);

    if (!slug) {
      setIsLoading(false);
      return;
    }

    const fetchFAQ = async () => {
      try {
        // Timeout de 10 segundos para evitar carregamento infinito
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Timeout: A busca demorou mais de 10 segundos'));
          }, 10000);
        });

        const queryPromise = supabase
          .from('politicas_content')
          .select('id, title, content, type, order_index, is_visible')
          .eq('is_visible', true)
          .or('deleted.is.null,deleted.eq.false')
          .order('order_index', { ascending: true });

        const { data: allFaqs, error: fetchError } = await Promise.race([
          queryPromise,
          timeoutPromise
        ]) as any;

        if (fetchError) {
          throw fetchError;
        }

        if (!allFaqs || allFaqs.length === 0) {
          setData(null);
          setIsLoading(false);
          return;
        }

        const faq = allFaqs.find(item => {
          const itemSlug = generateSlug(item.title);
          return itemSlug === slug;
        });

        if (!faq) {
          setData(null);
          setIsLoading(false);
          return;
        }

        const result: PoliticaContent = {
          id: faq.id,
          order_index: faq.order_index || 0,
          title: faq.title,
          content: faq.content as { html: string },
          is_visible: faq.is_visible,
          type: (faq.type || 'Geral') as PoliticaContentType
        };

        setData(result);
        setIsLoading(false);
      } catch (err) {
        setError(err as Error);
        setIsLoading(false);
      }
    };

    fetchFAQ();
  }, [slug]);

  return { data, isLoading, error };
};
